import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { sendChatMessage } from '../../lib/api';
import { useI18n } from '../../i18n';
import { company } from '../../mock';
import { Link, useNavigate } from 'react-router-dom';

const chatT = {
  en: {
    title: 'Hipro Assistant',
    subtitle: 'AI concierge · replies in seconds',
    hint: 'Ask anything about our services, pricing or process.',
    greeting: "👋 Hi, I'm the Hipro AI assistant. How can I help you today?",
    quickReplies: ['What services do you offer?', 'How much does an MVP cost?', 'Show me pricing packages', 'Book a call'],
    ph: 'Type your question…',
    error: 'Sorry, I could not reach the assistant. Please try again.',
    reset: 'Start new chat',
    aria: 'Open chat with Hipro Assistant',
    ariaClose: 'Close chat',
    poweredBy: 'AI-powered · in your language',
    bookCall: 'Book a free strategy call',
    disclaimer: 'AI responses may be inaccurate. For contracts and quotes, please confirm with a Hipro consultant.',
  },
  it: {
    title: 'Assistente Hipro',
    subtitle: 'Concierge AI · risposte in pochi secondi',
    hint: 'Chiedi qualsiasi cosa su servizi, prezzi o processo.',
    greeting: '👋 Ciao, sono l\'assistente AI di Hipro. Come posso aiutarti oggi?',
    quickReplies: ['Quali servizi offrite?', 'Quanto costa un MVP?', 'Mostrami i pacchetti', 'Prenota una chiamata'],
    ph: 'Scrivi la tua domanda…',
    error: 'Spiacente, non riesco a raggiungere l\'assistente. Riprova.',
    reset: 'Nuova chat',
    aria: 'Apri chat con Assistente Hipro',
    ariaClose: 'Chiudi chat',
    poweredBy: 'Basato su AI · nella tua lingua',
    bookCall: 'Prenota una chiamata strategica gratuita',
    disclaimer: 'Le risposte AI possono essere imprecise. Per contratti e preventivi, conferma con un consulente Hipro.',
  },
  hi: {
    title: 'Hipro सहायक',
    subtitle: 'AI कंसीयर्ज · कुछ सेकंडों में जवाब',
    hint: 'सेवाओं, कीमत या प्रक्रिया के बारे में कुछ भी पूछें।',
    greeting: '👋 नमस्ते, मैं Hipro AI सहायक हूँ। आज मैं आपकी कैसे मदद कर सकता हूँ?',
    quickReplies: ['आप कौन-सी सेवाएँ देते हैं?', 'MVP की क़ीमत क्या है?', 'पैकेज दिखाएँ', 'कॉल बुक करें'],
    ph: 'अपना प्रश्न लिखें…',
    error: 'माफ़ करें, सहायक से संपर्क नहीं हो पाया। कृपया फिर से प्रयास करें।',
    reset: 'नई चैट शुरू करें',
    aria: 'Hipro सहायक के साथ चैट खोलें',
    ariaClose: 'चैट बंद करें',
    poweredBy: 'AI-आधारित · आपकी भाषा में',
    bookCall: 'निःशुल्क रणनीति कॉल बुक करें',
    disclaimer: 'AI उत्तर अशुद्ध हो सकते हैं। अनुबंध और कोट के लिए, Hipro कंसल्टेंट से पुष्टि करें।',
  },
};

/** Very light Markdown -> HTML for chat bubbles */
const escapeHtml = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

const renderMarkdown = (text) => {
  let s = escapeHtml(text || '');
  // links
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer noopener" class="underline text-orange-600">$1</a>');
  // bold **text**
  s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
  // italic *text*
  s = s.replace(/(^|\s)\*([^*\n]+)\*/g, '$1<i>$2</i>');
  // inline code `code`
  s = s.replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-slate-800 px-1 rounded">$1</code>');
  // bullet lines
  s = s.replace(/(^|\n)[-•]\s([^\n]+)/g, '$1<div class="pl-3">• $2</div>');
  // paragraphs
  s = s.replace(/\n{2,}/g, '<br/><br/>');
  s = s.replace(/\n/g, '<br/>');
  return s;
};

const ChatWidget = () => {
  const { lang } = useI18n();
  const c = chatT[lang] || chatT.en;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try { return localStorage.getItem('hipro_chat_session') || null; } catch (e) { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Initial greeting message when opening for the first time (or after language change with empty history)
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', text: c.greeting, isGreeting: true }]);
    }
    // eslint-disable-next-line
  }, [open, lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setMessages((prev) => [...prev.filter((m) => !m.isGreeting), { role: 'user', text: message }, { role: 'assistant', text: '', typing: true }]);
    setSending(true);
    try {
      const res = await sendChatMessage({ session_id: session, message, lang });
      if (res.session_id && res.session_id !== session) {
        setSession(res.session_id);
        try { localStorage.setItem('hipro_chat_session', res.session_id); } catch (e) { /* ignore */ }
      }
      // Detect booking handoff marker
      let replyText = (res.reply || '').replace(/\[\[OPEN_BOOKING\]\]/g, '').trim();
      const shouldOpenBooking = /\[\[OPEN_BOOKING\]\]/.test(res.reply || '');
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.typing) copy.pop();
        copy.push({ role: 'assistant', text: replyText, openBooking: shouldOpenBooking });
        return copy;
      });
      if (shouldOpenBooking) {
        // Give the user a moment to read the message, then redirect
        setTimeout(() => {
          setOpen(false);
          navigate('/book-meeting');
        }, 1600);
      }
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.typing) copy.pop();
        copy.push({ role: 'assistant', text: c.error, isError: true });
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    try { localStorage.removeItem('hipro_chat_session'); } catch (e) { /* ignore */ }
    setSession(null);
    setMessages([{ role: 'assistant', text: c.greeting, isGreeting: true }]);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        aria-label={c.aria}
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full flex items-center justify-center shadow-xl transition-transform ${
          open
            ? 'bg-slate-900 hover:bg-slate-800'
            : 'bg-gradient-to-br from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
        }`}
      >
        {open ? <X className="h-6 w-6 text-white" /> : <MessageCircle className="h-6 w-6 text-white" />}
        {!open && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-40 w-[92vw] max-w-sm h-[560px] max-h-[80vh] rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white flex flex-col animate-[fade-up_.25s_ease-out]">
          {/* Header */}
          <div className="relative px-4 py-3 bg-slate-950 text-white overflow-hidden">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'radial-gradient(400px 200px at 0% 0%, rgba(249,115,22,0.35), transparent 60%), radial-gradient(400px 200px at 100% 100%, rgba(59,130,246,0.30), transparent 60%)',
              }}
            />
            <div className="relative flex items-center gap-3">
              <img src={company.logo} alt="Hipro" className="h-9 w-9 rounded object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-bold flex items-center gap-1.5 text-sm">
                  {c.title}
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full px-1.5 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Online
                  </span>
                </div>
                <div className="text-[11px] text-slate-300 truncate">{c.subtitle}</div>
              </div>
              <button onClick={reset} title={c.reset} className="p-1.5 rounded hover:bg-white/10 transition-colors">
                <RefreshCw className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} aria-label={c.ariaClose} className="p-1.5 rounded hover:bg-white/10 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center text-xs text-slate-400 mt-8">{c.hint}</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[86%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-slate-900 text-white rounded-br-sm'
                      : m.isError
                        ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-sm'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {m.typing ? (
                    <span className="inline-flex gap-1 py-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                    </span>
                  ) : (
                    <>
                      <span dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                      {m.openBooking && (
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-orange-600 font-semibold">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {lang === 'it' ? 'Apro il calendario…' : lang === 'hi' ? 'कैलेंडर खोल रहा हूँ…' : 'Opening the calendar…'}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick replies */}
          {messages.filter((m) => m.role === 'user').length === 0 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {c.quickReplies.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  disabled={sending}
                  className="text-xs bg-white border border-slate-200 hover:border-orange-400 hover:text-orange-600 text-slate-700 rounded-full px-2.5 py-1 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <div className="p-3 border-t border-slate-200 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={c.ph}
                disabled={sending}
                className="flex-1 resize-none max-h-24 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-1 focus:ring-orange-200 outline-none px-3 py-2 text-sm"
              />
              <button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="h-10 w-10 rounded-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
              <div className="flex items-center gap-1"><Sparkles className="h-3 w-3 text-orange-500" /> {c.poweredBy}</div>
              <Link to="/book-meeting" onClick={() => setOpen(false)} className="text-orange-600 hover:text-orange-700 font-semibold">
                {c.bookCall} →
              </Link>
            </div>
            <div className="mt-1 text-[10px] text-slate-400 leading-tight">{c.disclaimer}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
